import { forwardRef, useId, useMemo, useRef, useState } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "../lib/cn";
import { ChevronDownIcon, ClearIcon } from "../lib/icons";
import { Spinner } from "../atoms/Spinner";
import { Field, useFieldControlProps } from "./Field";
import type { ForwardedRef } from "react";

/**
 * Combobox — filterable single-select, mirroring MUI Autocomplete.
 *
 *   <Combobox
 *     label="Gang"
 *     placeholder="Search gangs…"
 *     options={[{ label: "A Team", value: "a" }]}
 *     value={gangId}
 *     onChange={setGangId}
 *   />
 *
 * The trigger is the search input (same frame as Field/Input): focusing it
 * opens the option list, typing filters, arrows + enter pick, click picks.
 * While untouched the full list shows — filtering starts with the first
 * keystroke. The X clears the selection and calls onChange(undefined).
 */

export interface ComboboxOption {
  /** Text shown in the list and in the input once selected. */
  label: string;
  /** Stable identifier passed to onChange. */
  value: string | number;
  /** Secondary line under the label (also matched while filtering). */
  description?: string;
  disabled?: boolean;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  /** Controlled selected option value. */
  value?: string | number;
  /** Called with the selected option value, or undefined when cleared. */
  onChange?: (value: string | number | undefined) => void;
  placeholder?: string;
  /** Label above the input; rendered through Field and wired to the control. */
  label?: string;
  /** Rendered when no option matches the typed text. */
  emptyText?: string;
  /** Swaps the option list for the loading row. */
  loading?: boolean;
  /** Text beside the spinner while loading. */
  loadingText?: string;
  disabled?: boolean;
  /** Error state — error border + aria-invalid. */
  invalid?: boolean;
  className?: string;
  "data-testid"?: string;
}

/** Input frame — the same chrome the Input atom's adornment wrapper uses. */
const frameClasses =
  "box-border flex h-10 w-full items-center gap-2 rounded-lg border border-border-control bg-surface-default px-3 transition-colors focus-within:border-border-focused focus-within:ring-2 focus-within:ring-border-focused-subtle";

const inputClasses =
  "h-full min-w-0 grow bg-transparent p-0 type-body-lg text-content-primary outline-none placeholder:text-content-disabled disabled:cursor-not-allowed";

/** Panel — the same popover grammar as the Menu atom. */
const panelClasses =
  "z-(--z-popover) overflow-hidden rounded-lg border border-border-default bg-surface-raised p-1 shadow-menu outline-none w-(--radix-popover-trigger-width) min-w-44";

function ComboboxListbox({
  options,
  emptyText,
  activeIndex,
  listId,
  onHover,
  onSelect,
}: {
  options: ComboboxOption[];
  emptyText: string;
  activeIndex: number;
  listId: string;
  onHover: (index: number) => void;
  onSelect: (option: ComboboxOption) => void;
}) {
  if (options.length === 0) {
    return (
      <div
        className="px-2 py-3 type-body-md text-content-secondary"
        role="status"
      >
        {emptyText}
      </div>
    );
  }
  return (
    <div
      role="listbox"
      id={listId}
      className="max-h-72 overflow-y-auto outline-none"
    >
      {options.map((option, index) => (
        <ComboboxOptionRow
          key={option.value}
          option={option}
          index={index}
          active={index === activeIndex}
          listId={listId}
          onHover={onHover}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

function ComboboxOptionRow({
  option,
  index,
  active,
  listId,
  onHover,
  onSelect,
}: {
  option: ComboboxOption;
  index: number;
  active: boolean;
  listId: string;
  onHover: (index: number) => void;
  onSelect: (option: ComboboxOption) => void;
}) {
  return (
    <div
      id={`${listId}-opt-${index}`}
      role="option"
      aria-selected={active}
      aria-disabled={option.disabled || undefined}
      tabIndex={-1}
      onMouseEnter={() => onHover(index)}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => {
        if (!option.disabled) {
          onSelect(option);
        }
      }}
      className={cn(
        "flex w-full cursor-default select-none flex-col items-start gap-0.5 rounded-md px-2 py-1.5 type-body-md outline-none",
        option.disabled
          ? "pointer-events-none text-content-disabled"
          : active
            ? "bg-surface-pressed text-content-primary"
            : "text-content-primary",
      )}
    >
      <span className="min-w-0">{option.label}</span>
      {option.description ? (
        <span className="type-caption text-content-secondary">
          {option.description}
        </span>
      ) : null}
    </div>
  );
}

function filterOptions(
  options: ComboboxOption[],
  search: string,
  typed: boolean,
) {
  if (!typed) {
    return options;
  }
  const needle = search.trim().toLowerCase();
  return options.filter((option) => {
    const haystack =
      `${option.label} ${option.value} ${option.description ?? ""}`.toLowerCase();
    return haystack.includes(needle);
  });
}

interface KeyHandlerContext {
  open: boolean;
  options: ComboboxOption[];
  activeIndex: number;
  openList: () => void;
  closeList: () => void;
  select: (option: ComboboxOption) => void;
  setActiveIndex: (index: number) => void;
}

function handleComboboxKeyDown(
  event: React.KeyboardEvent<HTMLInputElement>,
  context: KeyHandlerContext,
) {
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    if (!context.open) {
      context.openList();
      return;
    }
    const delta = event.key === "ArrowDown" ? 1 : -1;
    const count = Math.max(context.options.length, 1);
    context.setActiveIndex((context.activeIndex + delta + count) % count);
    return;
  }
  const activeOption = context.options[context.activeIndex];
  if (
    event.key === "Enter" &&
    context.open &&
    activeOption?.disabled !== true
  ) {
    event.preventDefault();
    if (activeOption) {
      context.select(activeOption);
    }
    return;
  }
  if (event.key === "Escape" && context.open) {
    event.preventDefault();
    context.closeList();
  }
}

interface ComboboxActionState {
  props: ComboboxProps;
  open: boolean;
  selectedLabel: string;
  setOpen: (open: boolean) => void;
  setSearch: (search: string) => void;
  setTyped: (typed: boolean) => void;
  setActiveIndex: (index: number) => void;
}

function useComboboxActions(state: ComboboxActionState) {
  const reset = (label: string) => {
    state.setSearch(label);
    state.setTyped(false);
    state.setActiveIndex(0);
  };
  const setOpenState = (next: boolean) => {
    if (state.props.disabled) {
      return;
    }
    state.setOpen(next);
    if (next) {
      reset(state.selectedLabel);
    }
  };
  const select = (option: ComboboxOption) => {
    state.props.onChange?.(option.value);
    reset(option.label);
    state.setOpen(false);
  };
  const clear = () => {
    state.props.onChange?.(undefined);
    reset("");
    state.setOpen(false);
  };
  const inputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    state.setSearch(event.currentTarget.value);
    state.setTyped(true);
    state.setActiveIndex(0);
  };
  const inputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.currentTarget.select();
    if (!state.open) {
      setOpenState(true);
    }
  };
  return { setOpenState, select, clear, inputChange, inputFocus };
}

function useComboboxModel(props: ComboboxProps) {
  const generatedId = useId();
  const listId = useId();
  const frameRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [typed, setTyped] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const fieldControlProps = useFieldControlProps({
    id: props.label ? generatedId : undefined,
    invalid: props.invalid,
  });
  const selected = props.options.find((option) => option.value === props.value);
  const selectedLabel =
    selected?.label ?? (props.value === undefined ? "" : String(props.value));
  const filtered = useMemo(
    () => filterOptions(props.options, search, typed),
    [props.options, search, typed],
  );
  const clampedIndex = Math.min(activeIndex, Math.max(filtered.length - 1, 0));
  const actions = useComboboxActions({
    props,
    open,
    selectedLabel,
    setOpen,
    setSearch,
    setTyped,
    setActiveIndex,
  });
  return {
    generatedId,
    listId,
    frameRef,
    fieldControlProps,
    open,
    search,
    typed,
    filtered,
    clampedIndex,
    selectedLabel,
    setSearch,
    setTyped,
    setActiveIndex,
    ...actions,
  };
}

type ComboboxModel = ReturnType<typeof useComboboxModel>;

function preventFrameDismiss(
  event: { preventDefault: () => void; target: EventTarget | null },
  model: ComboboxModel,
) {
  if (model.frameRef.current?.contains(event.target as Node)) {
    event.preventDefault();
  }
}

function ComboboxLoading({ text }: { text: string }) {
  return (
    <div
      role="status"
      className="flex items-center justify-center gap-2 px-2 py-3 type-body-md text-content-secondary"
    >
      <Spinner size={16} thickness={2} color="secondary" />
      {text}
    </div>
  );
}

function ComboboxPanel({
  props,
  model,
}: {
  props: ComboboxProps;
  model: ComboboxModel;
}) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align="start"
        sideOffset={4}
        collisionPadding={8}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
        onFocusOutside={(event) => {
          event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          preventFrameDismiss(event, model);
        }}
        onInteractOutside={(event) => {
          preventFrameDismiss(event, model);
        }}
        className={panelClasses}
      >
        {props.loading ? (
          <ComboboxLoading text={props.loadingText ?? "Loading…"} />
        ) : (
          <ComboboxListbox
            options={model.filtered}
            emptyText={props.emptyText ?? "No options"}
            activeIndex={model.clampedIndex}
            listId={model.listId}
            onHover={model.setActiveIndex}
            onSelect={model.select}
          />
        )}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
}

function ComboboxInput({
  props,
  model,
  inputRef,
}: {
  props: ComboboxProps;
  model: ComboboxModel;
  inputRef: ForwardedRef<HTMLInputElement>;
}) {
  const activeOption = model.filtered[model.clampedIndex];
  const keyContext: KeyHandlerContext = {
    open: model.open,
    options: model.filtered,
    activeIndex: model.clampedIndex,
    openList: () => model.setOpenState(true),
    closeList: () => model.setOpenState(false),
    select: model.select,
    setActiveIndex: model.setActiveIndex,
  };
  return (
    <input
      ref={inputRef}
      {...model.fieldControlProps}
      role="combobox"
      aria-expanded={model.open}
      aria-controls={model.open ? model.listId : undefined}
      aria-autocomplete="list"
      aria-activedescendant={
        model.open && activeOption
          ? `${model.listId}-opt-${model.clampedIndex}`
          : undefined
      }
      disabled={props.disabled}
      placeholder={props.placeholder}
      value={model.open ? model.search : model.selectedLabel}
      onChange={model.inputChange}
      onFocus={model.inputFocus}
      onKeyDown={(event) => {
        handleComboboxKeyDown(event, keyContext);
      }}
      className={inputClasses}
    />
  );
}

function ComboboxActions({
  props,
  model,
}: {
  props: ComboboxProps;
  model: ComboboxModel;
}) {
  const stopPointer = (event: React.PointerEvent) => event.stopPropagation();
  return (
    <>
      {props.value !== undefined && props.value !== "" ? (
        <button
          type="button"
          aria-label="Clear"
          onPointerDown={stopPointer}
          onClick={model.clear}
          className="flex shrink-0 cursor-default items-center rounded-sm text-content-secondary outline-none hover:text-content-primary focus-visible:ring-2 focus-visible:ring-border-focused"
        >
          <ClearIcon className="size-4" aria-hidden="true" />
        </button>
      ) : null}
      <button
        type="button"
        aria-label={model.open ? "Close list" : "Open list"}
        disabled={props.disabled}
        onPointerDown={stopPointer}
        onClick={() => model.setOpenState(!model.open)}
        className="flex shrink-0 cursor-default items-center rounded-sm text-content-secondary outline-none hover:text-content-primary focus-visible:ring-2 focus-visible:ring-border-focused"
      >
        <ChevronDownIcon
          aria-hidden="true"
          className={cn(
            "size-4 transition-transform duration-200",
            model.open && "rotate-180",
          )}
        />
      </button>
    </>
  );
}

function ComboboxFrame({
  props,
  model,
  inputRef,
}: {
  props: ComboboxProps;
  model: ComboboxModel;
  inputRef: ForwardedRef<HTMLInputElement>;
}) {
  return (
    <div
      ref={model.frameRef}
      data-slot="combobox"
      data-testid={props["data-testid"]}
      className={cn(
        frameClasses,
        model.open && "border-border-focused",
        props.invalid &&
          "border-surface-error focus-within:border-surface-error",
        props.disabled && "cursor-not-allowed opacity-38",
      )}
    >
      <ComboboxInput props={props} model={model} inputRef={inputRef} />
      <ComboboxActions props={props} model={model} />
    </div>
  );
}

function ComboboxControl({
  props,
  model,
  inputRef,
}: {
  props: ComboboxProps;
  model: ComboboxModel;
  inputRef: ForwardedRef<HTMLInputElement>;
}) {
  return (
    <PopoverPrimitive.Root open={model.open} onOpenChange={model.setOpenState}>
      <PopoverPrimitive.Anchor>
        <ComboboxFrame props={props} model={model} inputRef={inputRef} />
      </PopoverPrimitive.Anchor>
      <ComboboxPanel props={props} model={model} />
    </PopoverPrimitive.Root>
  );
}

export const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(
  (props, ref) => {
    const model = useComboboxModel(props);
    const control = (
      <ComboboxControl props={props} model={model} inputRef={ref} />
    );
    return props.label ? (
      <Field
        label={props.label}
        htmlFor={model.fieldControlProps.id ?? model.generatedId}
        className={props.className}
      >
        {control}
      </Field>
    ) : (
      <div className={cn("w-full", props.className)}>{control}</div>
    );
  },
);
Combobox.displayName = "Combobox";
